import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Zap, ArrowLeft, Wifi, WifiOff, Clock, TimerOff,
  Activity, Thermometer, Gauge, AlertTriangle, Wrench, Plus, X,
  Pencil, Check, Trash2, LayoutDashboard, Terminal, ScrollText, History,
  ArrowRight, QrCode, Radio, Loader2, KeyRound, Copy, RefreshCw, Eye, ListChecks,
} from 'lucide-react';
import { UserTypeahead } from '@/components/ui/UserTypeahead';
import { TimeWindowRows } from '@/components/ui/TimeWindowRows';
import { QrModal } from '@/components/ui/QrModal';
import type { GuestTimeWindowInput } from '@/lib/api';
import {
  fetchCharger, scheduleMaintenance, cancelMaintenance, deleteCharger, getChargerDeleteImpact,
  updateCharger, renameCharger, fetchLocations,
  fetchActiveSessionForCharger,
  convertChargerToResident, convertChargerToAdmin, releaseSelfRegistration,
  patchChargerConnectivity,
  revealChargerAuthPassword, regenerateChargerAuthPassword,
  fetchFleets, patchChargerFleetAssignment,
  getDisplayErrorMessage,
  listAgents,
  type ChargerDetail, type Location, type Connector,
  type AgentRow, type ChargerDeleteImpact,
} from '@/lib/api';
import {
  deriveChargerAccessMode, DEFAULT_CONNECTION_FEE_CENTS, CONNECTION_FEE_MAX_CENTS,
  chargerConnectivityLabel,
  type ChargerAccessMode,
} from '@evi/shared';
import {
  parseConnectionFeeDollars, connectionFeeCentsToDollars, formatConnectionFee,
} from '@/lib/connectionFeeInput';
import { useAuthStore } from '@/store/auth';
import { chargerTabsFor, isSiteCallerUser, omitUnreadEmpty } from '@/lib/siteDashboard';
import { mapListingBlockers } from '@/lib/mapListing';
import { chargersBackHref } from '@/lib/chargerScope';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { CommandsPanel } from './CommandsPanel';
import { ScopedSessionsList } from '@/components/sessions/ScopedSessionsList';
import { LogsPanel } from './LogsPanel';
import { AuditPanel } from './AuditPanel';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useLiveReload } from '@/components/LiveUpdatesProvider';
import { useStartTimeouts, formatTimeoutSummary, lastChargeLabel, siblingCableLabel } from '@/hooks/useStartTimeouts';
import { useStartSuccess } from '@/hooks/useStartSuccess';
import { completionLabel, formatStartSuccess, topCauses } from '@/lib/sessionOutcome';
import { StartAttemptsSection } from './StartAttemptsSection';
import { ocppConnectUrl } from '@/lib/ocppConnectUrl';
import styles from './ChargerDetailPage.module.css';
import { PrivateAccessSection } from './PrivateAccessSection';
import { ChargerAutoFixCard } from './ChargerAutoFixCard';
import { AlertSnoozeCard } from './AlertSnoozeCard';
import { TariffAssignmentCard } from '@/components/tariffs/TariffAssignmentCard';
import { Switch } from '@/components/ui/Switch';

interface ChargerDetailPageProps {
  chargerId: string;
  onNavigate: (path: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  Available: '#2ecc71',
  Charging: '#3498db',
  Preparing: '#f39c12',
  Finishing: '#9b59b6',
  Faulted: '#e74c3c',
  Unavailable: '#95a5a6',
  Reserved: '#e67e22',
  Occupied: '#3498db',
  SuspendedEV: '#f39c12',
  SuspendedEVSE: '#f39c12',
};

type ChargerTab = 'overview' | 'sessions' | 'commands' | 'logs' | 'audit';

const TABS: { key: ChargerTab; label: string; icon: typeof LayoutDashboard }[] = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  // This charger's sessions with who started each one (item 9, 2026-10-23).
  { key: 'sessions', label: 'Sessions', icon: ListChecks },
  { key: 'commands', label: 'Commands', icon: Terminal },
  { key: 'logs', label: 'Logs', icon: ScrollText },
  { key: 'audit', label: 'Audit', icon: History },
];

// Day-of-week chips and hour clamping moved into the shared
// TimeWindowRows component (Phase 7.2 extraction).

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ChargerDetailPage({ chargerId, onNavigate }: ChargerDetailPageProps) {
  // Two-Minute Window 4.5 — this charger's app-start timeout rate
  // (30d) from the shared dashboard read.
  const timeoutsQ = useStartTimeouts('30d');
  const chargerTimeouts = timeoutsQ.data?.chargers.find((r) => r.charger_id === chargerId);
  // Session success: this charger's start success rate (7d) against the 98%
  // target, from the shared dashboard read.
  const successQ = useStartSuccess('7d');
  const chargerSuccess = successQ.data?.chargers.find((r) => r.charger_id === chargerId);
  const [charger, setCharger] = useState<ChargerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<ChargerTab>('overview');
  const [maintModalOpen, setMaintModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [locationsList, setLocationsList] = useState<Location[]>([]);
  // Inline display-name editing state — distinct from the full edit
  // form because the rename uses a dedicated audit-logged endpoint.
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameErr, setRenameErr] = useState('');
  const [qrModalOpen, setQrModalOpen] = useState(false);
  // Charger-delete confirm modal (styled; replaces the native confirm/alert).
  // Holds the preflight impact (or a blocked reason) + the in-flight state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState<ChargerDeleteImpact | null>(null);
  const [deleteBlocked, setDeleteBlocked] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState('');
  // A Site persona reaches this page by link or typed URL (a location's
  // charger list, the Chargers list, a URL pasted or linked in an EVII
  // answer; the Site dashboard's own widgets send it to My Sites). The Api
  // refuses a Site the Logs and Audit reads and strips the pass list and
  // the owner from its charger read, so those surfaces are not rendered for
  // one (see isSiteCallerUser). Staff see the page exactly as before.
  const isSite = useAuthStore((s) => isSiteCallerUser(s.user));
  const isSuperAdminViewer = useAuthStore((s) => s.user?.role === 'super_admin');

  // Detail-page recents: empty label until data resolves, then
  // the charger identity. Hook re-fires on label change.
  useRecentRoute(charger ? `${charger.identity} (Charger)` : '');

  const load = () => {
    setLoading(true);
    fetchCharger(chargerId)
      .then(setCharger)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [chargerId]);

  // Live (28 Oct 2026, replaces the 15s poll): this charger's status and
  // its sessions refetch it in place. Held while editing or renaming so
  // the form is never clobbered; a change that landed meanwhile reloads
  // once the form closes.
  useLiveReload({
    kinds: ['charger', 'session'],
    match: (e) => e.charger_id === chargerId,
    enabled: !editing && !renaming,
    reload: () => { fetchCharger(chargerId).then(setCharger).catch(() => null); },
  });

  // Load dropdown options the first time edit is toggled on
  useEffect(() => {
    if (!editing || locationsList.length) return;
    fetchLocations({ limit: 100, include_residential_reporting: true }).then((r) => r.data).catch(() => [])
      .then((l) => { setLocationsList(l); });
  }, [editing, locationsList.length]);

  const startRename = () => {
    if (!charger) return;
    setRenameValue(charger.display_name ?? '');
    setRenameErr('');
    setRenaming(true);
  };

  const submitRename = async () => {
    if (!charger) return;
    const trimmed = renameValue.trim();
    // Server enforces ^[A-Za-z0-9 _-]{1,64}$; mirror it client-side
    // so the operator gets feedback before the round trip.
    if (trimmed && !/^[A-Za-z0-9 _-]{1,64}$/.test(trimmed)) {
      setRenameErr('Letters, numbers, space, underscore, hyphen. 1–64 chars.');
      return;
    }
    setRenameSaving(true);
    setRenameErr('');
    try {
      const updated = await renameCharger(charger._id, trimmed === '' ? null : trimmed);
      setCharger((prev) => (prev ? { ...prev, display_name: updated.display_name ?? null } : prev));
      setRenaming(false);
    } catch (err) {
      setRenameErr(err instanceof Error ? err.message : 'Rename failed');
    } finally {
      setRenameSaving(false);
    }
  };

  if (loading && !charger) {
    return <div className={styles.page}><p className={styles.loadingText}>Loading...</p></div>;
  }

  if (error && !charger) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{error}</div>
        <button className={styles.backBtn} onClick={() => onNavigate(chargersBackHref())}>
          <ArrowLeft size={16} /> Back to Chargers
        </button>
      </div>
    );
  }

  if (!charger) return null;

  const isConnected = charger.live_connection.connected;
  // Tabs a Site would only see a 403 in are not offered; a tab state left
  // on one of them (the persona resolved after a click) falls to Overview.
  const visibleTabs = chargerTabsFor(TABS, isSite);
  const activeTab: ChargerTab = visibleTabs.some((t) => t.key === tab) ? tab : 'overview';

  // Delete (deactivate) this charger. Opens the styled confirm modal and
  // preflights the impact INSIDE it -- the blocked case (active session) and the
  // cascade impact are shown in the admin's own Modal, never a native
  // alert/confirm. The actual write happens in confirmDelete on confirmation.
  const handleDelete = async (): Promise<void> => {
    setDeleteError('');
    setDeleteBlocked(null);
    setDeleteImpact(null);
    setDeleteBusy(true);
    setDeleteOpen(true);
    try {
      const pre = await getChargerDeleteImpact(chargerId);
      if (pre.blocked) {
        setDeleteBlocked(pre.block?.reason ?? 'This charger cannot be deleted right now.');
      } else {
        setDeleteImpact(pre.impact);
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to load delete impact.');
    } finally {
      setDeleteBusy(false);
    }
  };

  // Confirm the delete (only reachable when not blocked). Soft-deletes, then
  // navigates back to the list. Any error stays in the modal so it can be retried.
  const confirmDelete = async (): Promise<void> => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await deleteCharger(chargerId);
      setDeleteOpen(false);
      onNavigate(chargersBackHref());
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete charger.');
      setDeleteBusy(false);
    }
  };

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => onNavigate(chargersBackHref())}>
        <ArrowLeft size={16} /> Back to Chargers
      </button>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <Zap size={18} />
        </div>
        <div className={styles.headerInfo}>
          {renaming ? (
            <div className={styles.renameRow}>
              <input
                className={styles.renameInput}
                value={renameValue}
                placeholder={charger.identity}
                onChange={(e) => setRenameValue(e.target.value)}
                autoFocus
                disabled={renameSaving}
                maxLength={64}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void submitRename();
                  if (e.key === 'Escape') setRenaming(false);
                }}
              />
              <button
                className={styles.renameBtn}
                onClick={() => void submitRename()}
                disabled={renameSaving}
                title="Save"
              >
                <Check size={14} />
              </button>
              <button
                className={styles.renameBtn}
                onClick={() => setRenaming(false)}
                disabled={renameSaving}
                title="Cancel"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className={styles.titleRow}>
              <h1 className={styles.title}>{charger.display_name ?? charger.identity}</h1>
              <button
                className={styles.renameTrigger}
                onClick={startRename}
                title="Rename charger"
                aria-label="Rename charger"
              >
                <Pencil size={12} />
              </button>
            </div>
          )}
          {renameErr && <p className={styles.renameErr}>{renameErr}</p>}
          <p className={styles.subtitle}>
            <code className={styles.subIdentity}>{charger.identity}</code>
            {' '}· {charger.vendor} {charger.model}
            {charger.serial_number ? ` — S/N ${charger.serial_number}` : ''}
          </p>
          <p className={styles.locationLink} onClick={() => onNavigate(`/locations/${charger.location_id}`)}>
            {charger.location_name}
          </p>
        </div>
        <div className={styles.headerStatus}>
          <span className={`${styles.connectionBadge} ${isConnected ? styles.connected : styles.disconnected}`}>
            {isConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          {charger.firmware_version && (
            <span className={styles.fwBadge}>FW {charger.firmware_version}</span>
          )}
          <span className={styles.ocppBadge}>OCPP {charger.ocpp_version}</span>
          <button
            className={styles.editToggleBtn}
            onClick={() => setQrModalOpen(true)}
            title="View / download QR code for this charger"
          >
            <QrCode size={13} /> QR Code
          </button>
          {/* Not for a Site: the form's PUT is staff-only
              (requireInternalCaller), and it is seeded from a read the Api
              strips for a Site, so its guest pass list would go back empty.
              Rename (the pencil above) is PATCH and stays theirs. */}
          {!isSite && (
            <button
              className={styles.editToggleBtn}
              onClick={() => { setEditing((v) => !v); setEditErr(''); }}
              title={editing ? 'Cancel edit' : 'Edit charger'}
            >
              {editing ? <><X size={13} /> Cancel</> : <><Pencil size={13} /> Edit</>}
            </button>
          )}
          {/* Not for a Site: the Site templates carry no Charger:delete, so
              the delete-impact preflight is refused (403) inside the modal. */}
          {!isSite && (
            <button
              className={styles.editToggleBtn}
              style={{ color: '#dc2626' }}
              onClick={handleDelete}
              title="Delete (deactivate) this charger"
            >
              <Trash2 size={13} /> Delete
            </button>
          )}
        </div>
      </div>

      {editing && !isSite && (
        <ChargerEditForm
          charger={charger}
          locations={locationsList}
          saving={savingEdit}
          error={editErr}
          onCancel={() => { setEditing(false); setEditErr(''); }}
          onSave={async (patch) => {
            setSavingEdit(true);
            setEditErr('');
            try {
              const saved = await updateCharger(charger._id, patch);
              await load();
              // The PUT strips an unlisted key and still answers 200, so
              // an API that predates the connection fee drops it without
              // a word. Compare the echo with what was sent and keep the
              // form open on a mismatch; the other fields did save.
              if (
                patch.connection_fee_cents !== undefined
                && (saved.connection_fee_cents ?? null) !== patch.connection_fee_cents
              ) {
                setEditErr(
                  'Other changes saved, but the connection fee did not: the server '
                  + `returned ${formatConnectionFee(saved.connection_fee_cents)}. `
                  + 'The API may predate this field.',
                );
                return;
              }
              setEditing(false);
            } catch (e) {
              // Surface server-side VALIDATION_ERROR messages verbatim
              // (TOU overlap, end_hour > start_hour, etc.). Other
              // error codes fall back to the generic copy — see
              // getDisplayErrorMessage's rationale.
              setEditErr(await getDisplayErrorMessage(e, 'Failed to save'));
            } finally {
              setSavingEdit(false);
            }
          }}
        />
      )}

      {/* Tab strip */}
      <div className={styles.tabs} role="tablist">
        {visibleTabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            role="tab"
            aria-selected={activeTab === key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ''}`}
            onClick={() => setTab(key)}
          >
            <Icon size={14} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <>

      <DlmCapCard charger={charger} />

      {/* Automatic fixes on this charger (the remediation ladder): Super Admin; the Api says no to anyone else. */}
      {isSuperAdminViewer && <ChargerAutoFixCard chargerId={charger._id} />}

      {/* Snooze its alert emails (Warren, 29 Oct 2026, item 4): shown where the Api says a press would succeed. */}
      <AlertSnoozeCard chargerId={charger._id} />

      {/* Info cards */}
      <div className={styles.infoGrid}>
        <div className={styles.infoCard}>
          <Clock size={16} className={styles.infoIcon} />
          <div>
            <p className={styles.infoLabel}>Last Heartbeat</p>
            <p className={styles.infoValue}>
              {charger.last_heartbeat ? timeAgo(charger.last_heartbeat) : 'Never'}
            </p>
          </div>
        </div>
        <div className={styles.infoCard}>
          <Activity size={16} className={styles.infoIcon} />
          <div>
            <p className={styles.infoLabel}>Last Boot</p>
            <p className={styles.infoValue}>
              {charger.last_boot ? new Date(charger.last_boot).toLocaleDateString() : 'Never'}
            </p>
          </div>
        </div>
        {/* Two-Minute Window 4.5 — app starts this charger accepted where
            nobody plugged in before its plug-in window closed. Red when
            above 40% of 10+ starts. The second line is the number a site
            host needs: how long since anything actually charged here. */}
        {chargerTimeouts && (
          <div className={`${styles.infoCard} ${chargerTimeouts.flagged ? styles.errorCard : ''}`}>
            <TimerOff size={16} className={chargerTimeouts.flagged ? styles.errorIcon : styles.infoIcon} />
            <div>
              <p className={styles.infoLabel}>Start timeouts (30d)</p>
              <p className={styles.infoValue}>{formatTimeoutSummary(chargerTimeouts)}</p>
              <p className={styles.infoLabel}>
                {chargerTimeouts.sibling_cable > 0 ? `${siblingCableLabel(chargerTimeouts)} · ` : ''}
                {lastChargeLabel(chargerTimeouts.last_charged_at)}
                {chargerTimeouts.rejected > 0 ? ` · ${chargerTimeouts.rejected} start${chargerTimeouts.rejected === 1 ? '' : 's'} not accepted` : ''}
              </p>
            </div>
          </div>
        )}
        {/* Session success: started over started plus counted failures, red
            below the 98% target (or critical). Second line: the causes seen
            most, then session completion. Driver-side causes are not in the
            rate; for a Site they read as one. */}
        {chargerSuccess && (
          <div className={`${styles.infoCard} ${chargerSuccess.below_target || chargerSuccess.critical ? styles.errorCard : ''}`}>
            <Zap size={16} className={chargerSuccess.below_target || chargerSuccess.critical ? styles.errorIcon : styles.infoIcon} />
            <div>
              <p className={styles.infoLabel}>Start success (7d)</p>
              <p className={styles.infoValue}>{formatStartSuccess(chargerSuccess)}</p>
              <p className={styles.infoLabel}>
                {[topCauses(chargerSuccess.causes), completionLabel(chargerSuccess)].filter(Boolean).join(' · ') || 'Target 98%'}
              </p>
            </div>
          </div>
        )}
        {charger.diagnostics?.voltage !== undefined && (
          <div className={styles.infoCard}>
            <Gauge size={16} className={styles.infoIcon} />
            <div>
              <p className={styles.infoLabel}>Voltage</p>
              <p className={styles.infoValue}>{charger.diagnostics.voltage}V</p>
            </div>
          </div>
        )}
        {charger.diagnostics?.temperature !== undefined && (
          <div className={styles.infoCard}>
            <Thermometer size={16} className={styles.infoIcon} />
            <div>
              <p className={styles.infoLabel}>Temperature</p>
              <p className={styles.infoValue}>{charger.diagnostics.temperature}°C</p>
            </div>
          </div>
        )}
        {charger.diagnostics?.last_error_code && (
          <div className={`${styles.infoCard} ${styles.errorCard}`}>
            <AlertTriangle size={16} className={styles.errorIcon} />
            <div>
              <p className={styles.infoLabel}>Last Error</p>
              <p className={styles.infoValue}>{charger.diagnostics.last_error_code}</p>
            </div>
          </div>
        )}
        {/* OCPP command lifecycles — written by GetDiagnostics / UpdateFirmware
            dispatches and advanced by the charger's status notifications; the
            live charger events (useLiveReload above) keep them fresh. */}
        {charger.firmware_update?.status && (
          <div className={styles.infoCard}>
            <Activity size={16} className={styles.infoIcon} />
            <div>
              <p className={styles.infoLabel}>Firmware Update</p>
              <p className={styles.infoValue}>
                {charger.firmware_update.status}
                {charger.firmware_update.updated_at
                  ? ` · ${timeAgo(charger.firmware_update.updated_at)}`
                  : ''}
              </p>
            </div>
          </div>
        )}
        {charger.diagnostics_upload?.status && (
          <div className={styles.infoCard}>
            <Activity size={16} className={styles.infoIcon} />
            <div>
              <p className={styles.infoLabel}>Diagnostics Upload</p>
              <p className={styles.infoValue}>
                {charger.diagnostics_upload.status}
                {charger.diagnostics_upload.updated_at
                  ? ` · ${timeAgo(charger.diagnostics_upload.updated_at)}`
                  : ''}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pricing (Warren, 2026-10-25): "Use the location default" or a tariff
          of this charger's own, the tariff that bills now and where it comes
          from. Edit on Charger:update, never a Site; its own route and audit
          row (the edit form no longer sets the tariff). */}
      <TariffAssignmentCard kind="charger" id={charger._id} onNavigate={onNavigate} />

      {/* Session success: every start on this charger with its cause and fix. */}
      <StartAttemptsSection chargerId={charger._id} onNavigate={onNavigate} />

      {/* Connectors */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Connectors</h2>
        <div className={styles.connectorGrid}>
          {charger.connectors.map((conn) => (
            <div key={conn.connector_id} className={styles.connectorCard}>
              <div className={styles.connectorHeader}>
                <span className={styles.connectorId}>#{conn.connector_id}</span>
                <span
                  className={styles.connectorStatus}
                  style={{ color: STATUS_COLORS[conn.status] ?? '#95a5a6' }}
                >
                  <span
                    className={styles.connectorDot}
                    style={{ background: STATUS_COLORS[conn.status] ?? '#95a5a6' }}
                  />
                  {conn.status}
                </span>
              </div>
              <div className={styles.connectorDetails}>
                <span>{conn.type}</span>
                <span>{conn.max_power_kw} kW</span>
              </div>
              {conn.status_updated_at && (
                <p className={styles.connectorUpdated}>
                  Updated {timeAgo(conn.status_updated_at)}
                </p>
              )}
              <ActiveSessionPill
                chargerId={charger._id}
                connectorId={conn.connector_id}
                onNavigate={onNavigate}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Phase 5a-patch Item 4 — Connectivity / SIM provider.
          Operator asserts which provider this charger's cellular
          modem is on; this drives the 1nce webhook + reconciliation +
          rule 6/7 gates. */}
      <ConnectivitySection
        charger={charger}
        onSaved={(next) => setCharger(next)}
      />

      {/* OCPP credentials — reveal / regenerate the Basic-auth password.
          Self-contained, mirrors the Connectivity section; relieves the
          show-once password from charger creation. */}
      <OcppCredentialsSection charger={charger} />

      {/* Charger management type — admin-managed vs resident-managed.
          Convert-to-resident is the common path (used both for
          testing and for migrating chargers ops registered on
          behalf of a resident). Reverse exists for cleanup. */}
      {/* Not for a Site: it names the resident owner (created_by_user_*,
          stripped from a Site's read), and both convert routes are
          staff-only (requireInternalCaller). */}
      {!isSite && (
        <ChargerManagementTypeSection
          charger={charger}
          onConverted={(next) => setCharger(next)}
          onReleased={load}
        />
      )}

      {/* Fleet Mode Phase 4 — fleet attribution + mixed-use access.
          Sits adjacent to the Management Type section because both
          describe charger ownership/billing routing (one is admin
          vs resident provenance; this is which fleet bills the
          sessions, when fleet-attributed). Self-contained section
          mirrors the Connectivity / Management Type pattern: own
          state, own save handler, own PATCH endpoint. */}
      <FleetAssignmentSection
        charger={charger}
        onSaved={(next) => setCharger(next)}
      />

      {/* The private switch and the pass list (Warren, 2026-10-22). Own
          route, own audit row, its own save; the pass list is edited only
          here. */}
      <PrivateAccessSection
        chargerId={charger._id}
        privateAccess={charger.private_access === true}
        onSaved={load}
      />

      {/* Quick "see all commands" pointer — full surface lives in the
          dedicated tab so the overview stays scannable. */}
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Remote Commands</h2>
        <p className={styles.cmdPointer}>
          {charger.is_online
            ? <>Open the <button className={styles.cmdPointerBtn} onClick={() => setTab('commands')}>Commands</button> tab to dispatch OCPP actions.</>
            : <><WifiOff size={14} /> Charger is offline — dispatch will be rejected. Commands tab still shows the full surface.</>}
        </p>
      </div>

      {/* Maintenance windows */}
      <div className={styles.section}>
        <div className={styles.maintHeader}>
          <h2 className={styles.sectionTitle}><Wrench size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Scheduled Maintenance</h2>
          <button className={styles.maintAddBtn} onClick={() => setMaintModalOpen(true)}>
            <Plus size={14} /> Schedule Maintenance
          </button>
        </div>
        {(charger.maintenance_windows ?? []).length === 0 ? (
          <p className={styles.maintEmpty}>No maintenance windows scheduled.</p>
        ) : (
          <div className={styles.maintList}>
            {(charger.maintenance_windows ?? []).map((w) => {
              const now = new Date();
              const start = new Date(w.start);
              const end = new Date(w.end);
              const active = start <= now && now <= end;
              const past = end < now;
              return (
                <div key={w._id} className={`${styles.maintItem} ${active ? styles.maintActive : ''} ${past ? styles.maintPast : ''}`}>
                  <div className={styles.maintInfo}>
                    <div className={styles.maintTime}>
                      {start.toLocaleString()} → {end.toLocaleString()}
                      {active && <span className={styles.maintNowBadge}>NOW</span>}
                      {past && <span className={styles.maintPastBadge}>PAST</span>}
                    </div>
                    <div className={styles.maintReason}>{w.reason}</div>
                  </div>
                  {!past && w._id && (
                    <button
                      className={styles.maintCancelBtn}
                      onClick={async () => {
                        if (!confirm('Cancel this maintenance window?')) return;
                        try {
                          await cancelMaintenance(charger._id, w._id!);
                          load();
                        } catch (err) {
                          alert(err instanceof Error ? err.message : 'Failed to cancel');
                        }
                      }}
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      </>}

      {activeTab === 'sessions' && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Sessions</h2>
          <ScopedSessionsList chargerId={charger._id} onNavigate={onNavigate} />
        </div>
      )}

      {activeTab === 'commands' && (
        <CommandsPanel
          chargerId={charger._id}
          chargerIdentity={charger.identity}
          isOnline={charger.is_online}
          lastSeen={charger.last_heartbeat ? timeAgo(charger.last_heartbeat) : null}
        />
      )}

      {/* activeTab is never 'logs' or 'audit' for a Site (chargerTabsFor). */}
      {activeTab === 'logs' && (
        <LogsPanel chargerId={charger._id} chargerIdentity={charger.identity} />
      )}

      {activeTab === 'audit' && <AuditPanel chargerId={charger._id} />}

      {qrModalOpen && (
        <QrModal
          chargerIdentity={charger.identity}
          locationId={String(charger.location_id)}
          locationName={charger.location_name}
          onClose={() => setQrModalOpen(false)}
        />
      )}

      <Modal
        isOpen={maintModalOpen}
        onClose={() => setMaintModalOpen(false)}
        title="Schedule Maintenance"
        size="md"
      >
        <MaintenanceForm
          chargerId={charger._id}
          onSuccess={() => { setMaintModalOpen(false); load(); }}
          onCancel={() => setMaintModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={deleteOpen}
        onClose={() => { if (!deleteBusy) setDeleteOpen(false); }}
        title="Delete charger"
        size="sm"
        footer={
          deleteBlocked || (deleteError && !deleteImpact) ? (
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={() => setDeleteOpen(false)}
              disabled={deleteBusy}
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setDeleteOpen(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.deleteConfirmBtn}
                onClick={() => void confirmDelete()}
                disabled={deleteBusy || !deleteImpact}
              >
                {deleteBusy ? 'Deleting...' : 'Delete charger'}
              </button>
            </>
          )
        }
      >
        {deleteBusy && !deleteImpact && !deleteBlocked && !deleteError && (
          <p className={styles.deleteBlocked}>Checking impact...</p>
        )}
        {deleteBlocked && <p className={styles.deleteBlocked}>{deleteBlocked}</p>}
        {deleteImpact && (
          <div className={styles.deleteImpact}>
            <p>
              Delete charger{' '}
              <strong>&ldquo;{deleteImpact.display_name ?? deleteImpact.identity}&rdquo;</strong>?
              {' '}This deactivates the charger (soft delete -- history is kept):
            </p>
            <ul>
              <li>{deleteImpact.sessions_preserved} past session(s) are preserved</li>
              <li>
                removed from {deleteImpact.in_circuit ? 'its circuit, ' : ''}
                {deleteImpact.groups_dereferenced} group(s),{' '}
                {deleteImpact.access_assignments_dereferenced} access list(s)
              </li>
              {deleteImpact.is_online && (
                <li className={styles.deleteImpactNote}>
                  note: this charger is currently online
                </li>
              )}
            </ul>
          </div>
        )}
        {deleteError && <p className={styles.deleteError}>{deleteError}</p>}
      </Modal>
    </div>
  );
}

interface MaintenanceFormProps {
  chargerId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

function MaintenanceForm({ chargerId, onSuccess, onCancel }: MaintenanceFormProps) {
  // Default: tomorrow 09:00 → 17:00 local
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  tomorrow.setHours(9, 0, 0, 0);
  const tomorrowEnd = new Date(tomorrow);
  tomorrowEnd.setHours(17, 0, 0, 0);

  const isoLocal = (d: Date) => {
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };

  const [start, setStart] = useState(isoLocal(tomorrow));
  const [end, setEnd] = useState(isoLocal(tomorrowEnd));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErr('');
    try {
      await scheduleMaintenance(chargerId, {
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        reason: reason.trim(),
      });
      onSuccess();
    } catch (e) {
      setErr(await getDisplayErrorMessage(e, 'Failed to schedule'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={formStyles.form} onSubmit={handleSubmit}>
      {err && <div className={formStyles.formError}>{err}</div>}
      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Start</label>
          <input className={formStyles.input} type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} required />
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>End</label>
          <input className={formStyles.input} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} required />
        </div>
      </div>
      <div className={formStyles.field}>
        <label className={`${formStyles.label} ${formStyles.required}`}>Reason</label>
        <input
          className={formStyles.input}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Firmware update, hardware service, planned downtime"
          required
          maxLength={500}
        />
      </div>
      <p className={formStyles.hint} style={{ margin: 0 }}>
        During this window, the OCPP server will reject new charging sessions on this charger.
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
        <button type="button" className={formStyles.btnSecondary} onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="submit" className={formStyles.btnPrimary} disabled={submitting || !reason}>
          {submitting ? 'Scheduling...' : 'Schedule'}
        </button>
      </div>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Charger management type — admin-managed ↔ resident-managed
// ══════════════════════════════════════════════════════════════════════════

// ── OCPP credentials — reveal / regenerate the Basic-auth password ──────────
//
// Relieves the show-once password from charger creation. Reveal returns the
// stored plaintext (the value already on the charger keeps working);
// regenerate mints a new one and warns the hardware must be reconfigured with
// it before it will connect again. Self-contained, mirrors the Connectivity
// section. The server gates both via requireTenantScope (super_admin any
// tenant, tenant_admin own); the controls show only for those roles. The
// connect URL mirrors the one the create form shows verbatim.

interface OcppCredentialsSectionProps {
  charger: ChargerDetail;
}

function OcppCredentialsSection({ charger }: OcppCredentialsSectionProps) {
  const user = useAuthStore((s) => s.user);
  // A Site can hold a tenant_admin token (pickSessionRole takes the higher
  // tier); requireTenantScope refuses it as a scoped persona, so the
  // controls are not offered. Staff are unchanged (isSiteCallerUser is false).
  const isSite = isSiteCallerUser(user);
  const canManage = !isSite && (user?.role === 'tenant_admin' || user?.role === 'super_admin');

  const [password, setPassword] = useState<string | null>(null);
  const [rotated, setRotated] = useState(false);
  const [busy, setBusy] = useState<'reveal' | 'regenerate' | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The real per-stage websocket base (VITE_OCPP_PUBLIC_WS_URL), not a
  // placeholder — this is what the operator types into the unit.
  const connectUrl = ocppConnectUrl(charger.identity);

  const handleReveal = async () => {
    setBusy('reveal');
    setError(null);
    try {
      const res = await revealChargerAuthPassword(charger._id);
      setPassword(res.auth_password);
      setRotated(false);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not reveal the password'));
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm(
      'Regenerate the OCPP password? The current password stops working immediately. '
      + 'You must reconfigure the charger with the new password before it can connect again.',
    )) {
      return;
    }
    setBusy('regenerate');
    setError(null);
    try {
      const res = await regenerateChargerAuthPassword(charger._id);
      setPassword(res.auth_password);
      setRotated(true);
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Could not regenerate the password'));
    } finally {
      setBusy(null);
    }
  };

  const handleCopy = () => {
    if (!password) return;
    void navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }).catch(() => undefined);
  };

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>
        <KeyRound size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        OCPP credentials
      </h2>

      <div style={{
        display: 'grid', gridTemplateColumns: 'var(--admin-definition-columns, auto 1fr)', gap: 10,
        alignItems: 'center', fontSize: 13, marginBottom: 12,
      }}>
        <label style={{ color: 'var(--color-tx-m)' }}>Identity</label>
        <span><code>{charger.identity}</code></span>

        <label style={{ color: 'var(--color-tx-m)' }}>Connect URL</label>
        <span><code>{connectUrl}</code></span>

        <label style={{ color: 'var(--color-tx-m)' }}>Auth password</label>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <code>{password ?? '••••••••••••'}</code>
          {password && (
            <button
              type="button"
              onClick={handleCopy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', fontSize: 12,
                background: 'transparent', color: 'var(--color-tx-m)',
                border: '1px solid var(--color-brd, var(--evi-border))',
                borderRadius: 6, cursor: 'pointer',
              }}
            >
              {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          )}
        </span>
      </div>

      {canManage ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleReveal}
            disabled={busy !== null}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', fontSize: 13,
              background: 'transparent', color: 'var(--evi-accent, #2ECC71)',
              border: '1px solid var(--evi-accent, #2ECC71)', borderRadius: 8,
              fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy === 'reveal'
              ? <><Loader2 size={12} className="spin" /> Revealing…</>
              : <><Eye size={13} /> Reveal</>}
          </button>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={busy !== null}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', fontSize: 13,
              background: 'var(--evi-accent, #2ECC71)', color: '#000',
              border: 'none', borderRadius: 8, fontWeight: 600,
              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
            }}
          >
            {busy === 'regenerate'
              ? <><Loader2 size={12} className="spin" /> Regenerating…</>
              : <><RefreshCw size={13} /> Regenerate</>}
          </button>
          {error && (
            <span style={{ color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>{error}</span>
          )}
        </div>
      ) : (
        <p style={{ fontSize: 11, color: 'var(--color-tx-m)', margin: 0 }}>
          Only a tenant admin or super admin can reveal or regenerate the password.
        </p>
      )}

      {rotated && password && (
        <p style={{
          marginTop: 10, padding: '8px 12px', fontSize: 12, lineHeight: 1.5,
          background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 12%, transparent)',
          border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 35%, transparent)',
          borderRadius: 8, color: 'var(--evi-warning, #f59e0b)',
        }}>
          <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          New password set. Reconfigure the charger with this value before it will connect
          again. It will not be shown again once you leave this page.
        </p>
      )}

      <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 10 }}>
        OCPP Basic auth: configure the charger with this identity and password, then point it
        at the connect URL above.
      </p>
    </div>
  );
}

// ── Phase 5a-patch Item 4 — Connectivity section ────────────────────

interface ConnectivitySectionProps {
  charger: ChargerDetail;
  onSaved: (next: ChargerDetail) => void;
}

/**
 * Operator-asserted SIM provider + ICCID + read-only SIM status.
 *
 * Edit permissions: tenant_admin + super_admin can save changes;
 * fleet_admin and resident-owners get a read-only view. Resident-
 * owners only ever see this page if they own the charger (handled
 * upstream by the page-level auth gate), so we don't re-check
 * ownership here — just whether the role can mutate.
 */
function ConnectivitySection({ charger, onSaved }: ConnectivitySectionProps) {
  const user = useAuthStore((s) => s.user);
  // Read-only for a Site, whatever its token tier: a third party writes
  // display name and photo only (Warren 2026-09-25), and the Api refuses
  // the SIM fields to one under a tenant_admin token (requireInternalCaller).
  const isSite = isSiteCallerUser(user);
  const canEdit = !isSite && (user?.role === 'tenant_admin' || user?.role === 'super_admin');

  const initialProvider: '1nce' | 'other' | 'none' = charger.sim_provider === '1nce'
    ? '1nce'
    : charger.sim_provider === 'other'
      ? 'other'
      : 'none';
  const [provider, setProvider] = useState<'1nce' | 'other' | 'none'>(initialProvider);
  const [iccid, setIccid] = useState<string>(charger.sim_iccid ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    provider !== initialProvider
    || iccid !== (charger.sim_iccid ?? '');

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const patchBody: Parameters<typeof patchChargerConnectivity>[1] = {};
      if (provider !== initialProvider) {
        patchBody.sim_provider = provider === 'none' ? null : provider;
      }
      const trimmedIccid = iccid.trim();
      if (trimmedIccid !== (charger.sim_iccid ?? '')) {
        patchBody.sim_iccid = trimmedIccid === '' ? null : trimmedIccid;
      }
      const updated = await patchChargerConnectivity(charger._id, patchBody);
      // patch route returns a Charger doc; merge into existing
      // ChargerDetail (location_name + live_connection don't change).
      onSaved({ ...charger, ...updated });
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const simStatus = charger.diagnostics?.sim_status ?? 'unknown';
  const simLastEvent = charger.diagnostics?.sim_last_event_at;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>
        <Radio size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        Connectivity
      </h2>

      <div style={{
        display: 'grid', gridTemplateColumns: 'var(--admin-definition-columns, auto 1fr)', gap: 10,
        alignItems: 'center', fontSize: 13, marginBottom: 12,
      }}>
        {/* The deployment's connectivity choice (2026-10-01). Read-only. */}
        <label style={{ color: 'var(--color-tx-m)' }}>Connection</label>
        <span>
          {chargerConnectivityLabel(charger.connectivity)}
          {charger.connectivity && charger.connectivity_source === 'deployment' && (
            <span style={{ color: 'var(--color-tx-m)', fontSize: 11 }}> (from the deployment)</span>
          )}
        </span>

        <label style={{ color: 'var(--color-tx-m)' }}>SIM provider</label>
        {canEdit ? (
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value as '1nce' | 'other' | 'none')}
            disabled={saving}
            style={{ padding: 6, fontSize: 13, width: 160 }}
          >
            <option value="1nce">1nce</option>
            <option value="other">Other</option>
            <option value="none">None / unset</option>
          </select>
        ) : (
          <span>{charger.sim_provider ?? '—'}</span>
        )}

        <label style={{ color: 'var(--color-tx-m)' }}>SIM ICCID</label>
        {canEdit ? (
          <input
            type="text"
            value={iccid}
            onChange={(e) => setIccid(e.target.value)}
            placeholder="19-20 digit ICCID (auto-captured from BootNotification)"
            disabled={saving}
            style={{ padding: 6, fontSize: 13, width: 320 }}
          />
        ) : (
          <span><code>{charger.sim_iccid ?? '—'}</code></span>
        )}

        <label style={{ color: 'var(--color-tx-m)' }}>SIM IMSI</label>
        <span><code>{charger.sim_imsi ?? '—'}</code> <span style={{ color: 'var(--color-tx-m)', fontSize: 11 }}>(set by BootNotification)</span></span>

        {provider === '1nce' && (
          <>
            <label style={{ color: 'var(--color-tx-m)' }}>1nce status</label>
            <span>
              <span style={{
                display: 'inline-block', padding: '2px 8px',
                fontSize: 11, borderRadius: 999,
                background: simStatus === 'online'
                  ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 16%, transparent)'
                  : simStatus === 'offline'
                    ? 'color-mix(in srgb, var(--evi-error, #ef4444) 16%, transparent)'
                    : 'color-mix(in srgb, var(--color-tx-m) 16%, transparent)',
                color: simStatus === 'online'
                  ? 'var(--evi-accent, #2ECC71)'
                  : simStatus === 'offline'
                    ? 'var(--evi-error, #ef4444)'
                    : 'var(--color-tx-m)',
              }}>
                {simStatus}
              </span>
              {simLastEvent && (
                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-tx-m)' }}>
                  · last event {timeAgo(simLastEvent)}
                </span>
              )}
            </span>
          </>
        )}
      </div>

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || saving}
            style={{
              padding: '8px 14px', fontSize: 13,
              background: 'var(--evi-accent, #2ECC71)', color: '#000',
              border: 'none', borderRadius: 8, fontWeight: 600,
              cursor: dirty && !saving ? 'pointer' : 'not-allowed',
              opacity: dirty && !saving ? 1 : 0.5,
            }}
          >
            {saving ? <><Loader2 size={12} className="spin" /> Saving…</> : 'Save connectivity'}
          </button>
          {error && (
            <span style={{ color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
              {error}
            </span>
          )}
        </div>
      )}

      <p style={{ fontSize: 11, color: 'var(--color-tx-m)', marginTop: 10 }}>
        Provider gates SIM-based predictive-maintenance rules. Only chargers with provider <code>1nce</code> participate in 1nce webhook + reconciliation flows. See <a href="/docs/operations/sim-provider-migration" style={{ color: 'var(--evi-accent, #2ECC71)' }}>migration guide</a>.
      </p>
    </div>
  );
}

// ── Fleet Mode Phase 4 — Fleet Assignment section ──────────────────

interface FleetAssignmentSectionProps {
  charger: ChargerDetail;
  onSaved: (next: ChargerDetail) => void;
}

/**
 * Admin operator surface for charger.fleet_id + charger.allow_mixed_use.
 *
 * Three sub-cards:
 *   1. Assign to Fleet — flat dropdown of in-tenant fleets +
 *      "(No fleet — public charger)" sentinel. Flat dropdown chosen
 *      over a typeahead because (a) no FleetTypeahead exists in the
 *      codebase yet, and (b) the v1 deployment has dozens of fleets
 *      per tenant max — well within scrollable-dropdown territory. If
 *      a tenant ever exceeds ~50 fleets we'd extract a typeahead
 *      mirroring UserTypeahead's shape.
 *   2. Mixed-Use Access — three-state radio (inherit / fleet_only /
 *      mixed). Renders only when a fleet is assigned (no fleet =
 *      nothing to inherit). Maps to allow_mixed_use: null / false /
 *      true respectively. The `inherit` copy reads the parent fleet's
 *      `allows_mixed_use_default` so the operator sees what they're
 *      inheriting before saving.
 *   3. Effective Access Mode — read-only badge computed via
 *      deriveChargerAccessMode (Phase 1 helper). Rerenders on every
 *      local state change so the operator can preview what the
 *      access gate will actually do BEFORE saving.
 *
 * Permission gate matches Charger:update (server enforces; client
 * mirrors so non-tenant-admins see a read-only view).
 */
function FleetAssignmentSection({ charger, onSaved }: FleetAssignmentSectionProps) {
  const user = useAuthStore((s) => s.user);
  // Read-only for a Site, whatever its token tier: a third party writes
  // display name and photo only (Warren 2026-09-25), and the Api refuses
  // this route to one under a tenant_admin token (requireInternalCaller).
  const isSite = isSiteCallerUser(user);
  const canEdit = !isSite && (user?.role === 'tenant_admin' || user?.role === 'super_admin');

  // Fleets list — tenant-scoped per the existing GET /admin/fleets
  // route. Cached on the React Query default keys so re-opening the
  // page (or visiting another charger detail) doesn't re-fetch.
  const fleetsQ = useQuery({
    queryKey: ['admin', 'fleets', 'list-for-assignment'],
    queryFn: fetchFleets,
    staleTime: 60_000,
  });

  // ── Local form state ─────────────────────────────────────────────
  // 'none' is the sentinel for "no fleet assignment" — distinct from
  // any real fleet _id. The save handler maps it back to null.
  const initialFleetId = charger.fleet_id ?? null;
  const initialMixedUse = charger.allow_mixed_use ?? null; // null | true | false
  const [selectedFleetId, setSelectedFleetId] = useState<string | null>(initialFleetId);
  // Mixed-use as a discriminated string for the radio group; mapped
  // to true/false/null on save so the wire shape stays the schema's
  // three-state Boolean | null.
  const initialMixedUseChoice: 'inherit' | 'fleet_only' | 'mixed' =
    initialMixedUse === null ? 'inherit' : initialMixedUse ? 'mixed' : 'fleet_only';
  const [mixedUseChoice, setMixedUseChoice] = useState<'inherit' | 'fleet_only' | 'mixed'>(
    initialMixedUseChoice,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The currently-selected Fleet doc (used for inherit-default copy
  // + the effective-mode preview). Null when no fleet is selected
  // OR when the fleets fetch hasn't landed yet.
  const selectedFleet = selectedFleetId
    ? fleetsQ.data?.find((f) => f._id === selectedFleetId) ?? null
    : null;

  // A FLEET IS ASSIGNED AND WE COULD NOT LOAD IT — DO NOT GUESS.
  //
  // GET /admin/fleets 403s for any persona without fleet:read or
  // tenant:read (an operator, for one), and this query has no error
  // branch, so selectedFleet falls to null exactly as it does while the
  // fetch is in flight. Feeding null to deriveChargerAccessMode does
  // not produce "unknown" — it produces 'fleet_only', because a fleet
  // with no allows_mixed_use_default reads as false. So a charger that
  // INHERITS mixed use from its fleet was being labelled "Fleet only"
  // in confident capitals to the one persona least able to check.
  //
  // The badge is a claim about what the API gate will do. When the
  // input to that claim is missing, the honest badge is no badge.
  const fleetRecordUnavailable = Boolean(selectedFleetId) && !selectedFleet;

  // Convert the mixedUseChoice + selectedFleetId into the
  // (charger, fleet) shape deriveChargerAccessMode wants. The helper
  // is the same one the API gate uses, so the badge is exactly what
  // the gate would compute post-save.
  const previewAccessMode: ChargerAccessMode = deriveChargerAccessMode(
    {
      fleet_id: selectedFleetId,
      allow_mixed_use:
        mixedUseChoice === 'inherit' ? null : mixedUseChoice === 'mixed',
    },
    selectedFleet
      ? { allows_mixed_use_default: selectedFleet.allows_mixed_use_default ?? false }
      : null,
  );

  const dirty =
    selectedFleetId !== initialFleetId
    || mixedUseChoice !== initialMixedUseChoice;

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const body: Parameters<typeof patchChargerFleetAssignment>[1] = {};
      if (selectedFleetId !== initialFleetId) {
        body.fleet_id = selectedFleetId;
      }
      // allow_mixed_use is meaningful only when a fleet remains
      // assigned. If we're clearing the fleet, the server-side
      // side-effect resets allow_mixed_use to null too — don't send
      // it explicitly (saves a wire byte and matches server intent).
      if (selectedFleetId && mixedUseChoice !== initialMixedUseChoice) {
        body.allow_mixed_use =
          mixedUseChoice === 'inherit' ? null : mixedUseChoice === 'mixed';
      }
      const updated = await patchChargerFleetAssignment(charger._id, body);
      onSaved({ ...charger, ...updated });
    } catch (err) {
      setError(await getDisplayErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const accessBadgeColor =
    previewAccessMode === 'public'
      ? 'var(--evi-accent, #2ECC71)'
      : previewAccessMode === 'fleet_only'
        ? 'var(--evi-info, #3b82f6)'
        : 'var(--evi-power, #facc15)'; // mixed

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Fleet Assignment</h2>

      <div style={{
        display: 'grid', gridTemplateColumns: 'var(--admin-definition-columns, auto 1fr)', gap: 10,
        alignItems: 'center', fontSize: 13, marginBottom: 12,
      }}>
        <label style={{ color: 'var(--color-tx-m)' }}>Assigned fleet</label>
        {canEdit ? (
          <select
            value={selectedFleetId ?? '__none__'}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedFleetId(v === '__none__' ? null : v);
            }}
            disabled={saving || fleetsQ.isLoading}
            style={{ padding: 6, fontSize: 13, width: 320 }}
          >
            <option value="__none__">(No fleet — public charger)</option>
            {(fleetsQ.data ?? []).map((f) => (
              <option key={f._id} value={f._id}>
                {f.name}{!f.is_active ? ' (inactive)' : ''}
              </option>
            ))}
          </select>
        ) : (
          <span>{selectedFleet?.name ?? '—'}</span>
        )}

        {selectedFleetId && (
          <>
            <label style={{ color: 'var(--color-tx-m)' }}>Mixed-use access</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  name={`mixed-use-${charger._id}`}
                  checked={mixedUseChoice === 'inherit'}
                  onChange={() => setMixedUseChoice('inherit')}
                  disabled={!canEdit || saving}
                />
                <span>
                  Use fleet default
                  {selectedFleet && (
                    <span style={{ color: 'var(--color-tx-m)', marginLeft: 6 }}>
                      ({selectedFleet.allows_mixed_use_default ? 'mixed-use on' : 'fleet only'})
                    </span>
                  )}
                </span>
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  name={`mixed-use-${charger._id}`}
                  checked={mixedUseChoice === 'fleet_only'}
                  onChange={() => setMixedUseChoice('fleet_only')}
                  disabled={!canEdit || saving}
                />
                <span>Fleet only</span>
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  name={`mixed-use-${charger._id}`}
                  checked={mixedUseChoice === 'mixed'}
                  onChange={() => setMixedUseChoice('mixed')}
                  disabled={!canEdit || saving}
                />
                <span>Allow mixed use</span>
              </label>
              <p style={{ fontSize: 11, color: 'var(--color-tx-m)', margin: '4px 0 0', maxWidth: 520 }}>
                Mixed-use chargers can be used by fleet drivers (charged to the fleet)
                AND by drivers in public charging mode (charged personally). Fleet-only
                chargers refuse public-mode drivers outright.
              </p>
            </div>
          </>
        )}

        <label style={{ color: 'var(--color-tx-m)' }}>Effective access</label>
        <span>
          <span style={{
            display: 'inline-block', padding: '2px 10px',
            fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
            borderRadius: 999,
            background: `color-mix(in srgb, ${accessBadgeColor} 16%, transparent)`,
            color: accessBadgeColor,
            border: `1px solid color-mix(in srgb, ${accessBadgeColor} 35%, transparent)`,
            textTransform: 'uppercase',
          }}>
            {fleetRecordUnavailable
              ? 'Unknown'
              : previewAccessMode === 'fleet_only'
                ? 'Fleet only'
                : previewAccessMode === 'mixed'
                  ? 'Mixed use'
                  : 'Public'}
          </span>
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--color-tx-m)' }}>
            {fleetRecordUnavailable
              ? "(this charger's fleet could not be loaded, so its access mode cannot be shown)"
              : '(preview — saves on click below)'}
          </span>
        </span>
      </div>

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => { void handleSave(); }}
            disabled={!dirty || saving}
            style={{
              padding: '8px 14px', fontSize: 13,
              background: 'var(--evi-accent, #2ECC71)', color: '#000',
              border: 'none', borderRadius: 8, fontWeight: 600,
              cursor: dirty && !saving ? 'pointer' : 'not-allowed',
              opacity: dirty && !saving ? 1 : 0.5,
            }}
          >
            {saving ? <><Loader2 size={12} className="spin" /> Saving…</> : 'Save fleet assignment'}
          </button>
          {error && (
            <span style={{ color: 'var(--evi-error, #ef4444)', fontSize: 12 }}>
              {error}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

interface ChargerManagementTypeSectionProps {
  charger: ChargerDetail;
  onConverted: (next: ChargerDetail) => void;
  /** Reload after a release: the charger is deactivated and renamed. */
  onReleased: () => void;
}

function ChargerManagementTypeSection({
  charger, onConverted, onReleased,
}: ChargerManagementTypeSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [releasedNote, setReleasedNote] = useState('');
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'super_admin');

  const isResidentManaged = charger.created_via === 'resident_self_service';
  // Register-first squatting (2026-10-02): a resident registration that
  // never connected can hold a serial its real owner needs. Super admins
  // release it here; the Api re-checks every condition (no connection, no
  // session, no verified factory reservation) and refuses otherwise. A
  // deactivated registration qualifies too: the ordinary Delete keeps the
  // identity, so a deleted squatter's claim still blocks the serial. One
  // already released (renamed released-<id>-...) has nothing left to free.
  const neverConnected = !charger.last_heartbeat && !charger.last_boot;
  const alreadyReleased = charger.identity.startsWith(`released-${charger._id}-`);
  const canRelease = isSuperAdmin && isResidentManaged && neverConnected && !alreadyReleased;

  const handleRelease = async (): Promise<void> => {
    if (!window.confirm(
      `Release ${charger.identity}? The registration by ${charger.created_by_user_name ?? 'this resident'} `
      + 'is deactivated and the identity is freed so its real owner can register it. The registrant is told. '
      + 'This cannot be undone here.',
    )) {
      return;
    }
    setBusy(true);
    setErrorMsg('');
    try {
      const res = await releaseSelfRegistration(charger._id);
      setReleasedNote(`Released. ${res.identity} is free to register again.`);
      onReleased();
    } catch (err) {
      setErrorMsg(await getDisplayErrorMessage(err, 'Could not release this registration'));
    } finally {
      setBusy(false);
    }
  };

  const handleRevert = async (): Promise<void> => {
    setBusy(true);
    setErrorMsg('');
    try {
      const updated = await convertChargerToAdmin(charger._id);
      onConverted(updated);
      setConfirmRevert(false);
    } catch (err) {
      setErrorMsg(await mapConvertError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Charger Management Type</h2>

      <div className={styles.mgmtRow}>
        <div className={styles.mgmtState}>
          <p className={styles.mgmtLabel}>
            {isResidentManaged ? 'Resident-managed' : 'Admin-managed'}
          </p>
          {isResidentManaged ? (
            <p className={styles.mgmtBy}>
              by {charger.created_by_user_name ?? '(unknown user)'}
            </p>
          ) : (
            <p className={styles.mgmtBy}>
              Configured by ops. Convert to let a resident manage settings
              from the EVI app.
            </p>
          )}
        </div>
        <div className={styles.mgmtActions}>
          {!isResidentManaged && (
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={() => { setPickerOpen(true); setErrorMsg(''); }}
              disabled={busy}
            >
              Convert to resident-managed
            </button>
          )}
          {isResidentManaged && !confirmRevert && (
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={() => setConfirmRevert(true)}
              disabled={busy}
            >
              Revert to admin-managed
            </button>
          )}
          {canRelease && !confirmRevert && (
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={handleRelease}
              disabled={busy}
              title="Free this identity: the registration never connected"
            >
              Release registration
            </button>
          )}
          {isResidentManaged && confirmRevert && (
            <div className={styles.mgmtConfirmRow}>
              <span className={styles.mgmtConfirmText}>
                Revert {charger.created_by_user_name ?? 'the current owner'} to
                no longer manage this charger?
              </span>
              <button
                type="button"
                className={formStyles.btnSecondary}
                onClick={() => setConfirmRevert(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={formStyles.btnPrimary}
                onClick={handleRevert}
                disabled={busy}
              >
                {busy ? 'Reverting…' : 'Yes, revert'}
              </button>
            </div>
          )}
        </div>
      </div>

      {errorMsg && (
        <div className={formStyles.formError} role="alert">{errorMsg}</div>
      )}
      {releasedNote && (
        <p className={styles.mgmtBy} role="status">{releasedNote}</p>
      )}
      {canRelease && (
        <p className={styles.mgmtBy}>
          Never connected. If someone else registered this serial first, release it so the
          real owner can register it.
        </p>
      )}

      {pickerOpen && (
        <ConvertToResidentModal
          chargerId={charger._id}
          onClose={() => setPickerOpen(false)}
          onSuccess={(updated) => {
            onConverted(updated);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

interface ConvertToResidentModalProps {
  chargerId: string;
  onClose: () => void;
  onSuccess: (updated: ChargerDetail) => void;
}

function ConvertToResidentModal({
  chargerId, onClose, onSuccess,
}: ConvertToResidentModalProps) {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleConvert = async (): Promise<void> => {
    if (selectedUserIds.length === 0) {
      setErrorMsg('Pick a user to assign as the new owner.');
      return;
    }
    setBusy(true);
    setErrorMsg('');
    try {
      const updated = await convertChargerToResident(chargerId, selectedUserIds[0]!);
      onSuccess(updated);
    } catch (err) {
      setErrorMsg(await mapConvertError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Convert to resident-managed" size="md">
      <div className={styles.convertBody}>
        <p className={styles.convertLead}>
          Pick the resident who will own this charger. They&rsquo;ll see it
          in their <code>My Chargers</code> list and can manage settings
          (pass list, time windows, sharing) from the EVI app.
          The user must be in this building&rsquo;s tenant and have a phone
          on file.
        </p>

        <UserTypeahead
          selectedUserIds={selectedUserIds}
          onChange={setSelectedUserIds}
          maxSelections={1}
          placeholder="Search by email, username, or name…"
          ariaLabel="Pick the new charger owner"
        />

        {errorMsg && (
          <div className={formStyles.formError} role="alert">{errorMsg}</div>
        )}

        <div className={styles.convertActions}>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={formStyles.btnPrimary}
            onClick={handleConvert}
            disabled={busy || selectedUserIds.length === 0}
          >
            {busy ? 'Converting…' : 'Convert'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Map structured error codes from the convert endpoints to
 *  admin-friendly inline copy. Falls back to the API's own message
 *  field where present, then to a generic. */
async function mapConvertError(err: unknown): Promise<string> {
  const response = (err as { response?: Response }).response;
  if (!response) {
    return err instanceof Error ? err.message : 'Failed to convert charger.';
  }
  try {
    const body = await response.clone().json() as { error?: { code?: string; message?: string } };
    return body.error?.message ?? 'Failed to convert charger.';
  } catch {
    return 'Failed to convert charger.';
  }
}

// ══════════════════════════════════════════════════════════════════════════
// Inline edit form for charger core fields
// ══════════════════════════════════════════════════════════════════════════

interface ChargerEditFormProps {
  charger: ChargerDetail;
  locations: Location[];
  saving: boolean;
  error: string;
  onCancel: () => void;
  onSave: (patch: {
    vendor: string;
    model: string;
    ocpp_version: string;
    location_id: string;
    is_active: boolean;
    connectors: Connector[];
    // Third-party (guest) access fields. Sent on save (even when
    // unchanged) so the PATCH body carries the operator's current
    // intent and Mongoose's runValidators sees the full shape per
    // call — EXCEPT guest_markup_bps, which is omitted for a home
    // charger (residential location): the markup is a multifamily
    // concept and the server treats an absent key as no-touch.
    is_open_to_guests: boolean;
    guest_markup_bps?: number | null;
    public_network: boolean;
    // The pass list is never sent from this form (2026-10-23): the
    // Private access section owns it. Time windows round-trip.
    guest_time_windows: ChargerDetail['guest_time_windows'];
    // Network Origin (Phase 7b) — agent attribution. Only sent when
    // the operator is super_admin (the form hides the section
    // entirely for tenant_admin; the parent's updateCharger call
    // includes these fields in the payload regardless, and the
    // server PUT route strips them server-side for non-super_admin
    // callers as defense-in-depth). Optional on the same rule as
    // guest_pass_list: absent from the read and still empty = not sent.
    agent_id?: string | null;
    agent_commission_bps?: number | null;
    // EVI's connection fee (cents, null = no fee). super_admin only,
    // and present ONLY when changed: an untouched fee is a no-touch.
    connection_fee_cents?: number | null;
  }) => Promise<void>;
}

function ChargerEditForm({ charger, locations, saving, error, onCancel, onSave }: ChargerEditFormProps) {
  const [vendor, setVendor] = useState(charger.vendor);
  const [model, setModel] = useState(charger.model);
  const [ocppVersion, setOcppVersion] = useState(charger.ocpp_version);
  const [locationId, setLocationId] = useState(charger.location_id);
  const [isActive, setIsActive] = useState(charger.is_active);
  const [connectors, setConnectors] = useState<Connector[]>(() => charger.connectors.map((c) => ({ ...c })));
  // Guest access state. The pass list + time windows round-trip
  // unchanged in commit 9 (commits 10 + 11 wire their own UI),
  // captured on mount so Save doesn't accidentally clear them.
  const [isOpenToGuests, setIsOpenToGuests] = useState(charger.is_open_to_guests ?? false);
  // public_network defaults to true on the schema (commit f0a0c21).
  // Fallback `?? true` keeps a doc with an unexpectedly undefined
  // value rendering as the opt-out state the schema implies.
  const [publicNetwork, setPublicNetwork] = useState(charger.public_network ?? true);
  // Markup edited as a percentage in the input (0–500), persisted
  // as basis points (0–50000) in the schema. Empty string = null
  // = "not guest-eligible" (charger is open but unpriced is
  // functionally closed per canGuestUseCharger step 3 — server-
  // side gate enforces). Initial value: bps → percent string.
  const [markupPctStr, setMarkupPctStr] = useState(
    typeof charger.guest_markup_bps === 'number'
      ? (charger.guest_markup_bps / 100).toString()
      : '',
  );
  const [guestTimeWindows, setGuestTimeWindows] = useState<GuestTimeWindowInput[]>(
    charger.guest_time_windows ?? [],
  );

  // ── Network Origin (Phase 7b) ───────────────────────────────────
  // Super_admin-only state. The section that uses these hooks is
  // gated by `isSuperAdmin` further down so the hooks themselves
  // always run (React's rules-of-hooks); the parent's
  // updateCharger payload always includes the fields and the
  // server PUT route strips them for non-super_admin callers.
  const editorUser = useAuthStore((s) => s.user);
  const isSuperAdmin = editorUser?.role === 'super_admin';

  const [agentId, setAgentId] = useState<string>(charger.agent_id ?? '');
  // Edited as percent string, stored as bps — parallel to the
  // guest_markup_bps pattern above. Empty string when no agent
  // selected; cleared automatically when agentId clears.
  const [commissionPctStr, setCommissionPctStr] = useState(
    typeof charger.agent_commission_bps === 'number'
      ? (charger.agent_commission_bps / 100).toString()
      : '',
  );

  // Lazy-load agent list when the section first renders for a
  // super_admin. Tenant_admin never triggers this fetch.
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsLoadErr, setAgentsLoadErr] = useState<string>('');
  useEffect(() => {
    if (!isSuperAdmin || agents !== null || agentsLoading) return;
    setAgentsLoading(true);
    listAgents()
      .then((rows) => setAgents(rows))
      .catch(async (err: unknown) => {
        setAgentsLoadErr(await getDisplayErrorMessage(err, 'Failed to load Sales Partners'));
      })
      .finally(() => setAgentsLoading(false));
  }, [isSuperAdmin, agents, agentsLoading]);

  // Commission percent string → bps for the wire. Empty / no-agent
  // → null. Caps at 50000 (500%) at the form layer; schema enforces
  // the same. Mirrors markupBpsForSave above.
  const commissionBpsForSave = (() => {
    if (!agentId) return null;
    const trimmed = commissionPctStr.trim();
    if (!trimmed) return null;
    const pct = Number(trimmed);
    if (!Number.isFinite(pct) || pct < 0) return null;
    return Math.round(pct * 100);
  })();

  // EVI's connection fee (Tier 2). Edited in dollars, stored as cents;
  // blank = null = no fee. Sent only when it differs from the stored
  // value, unlike the always-sent agent fields: an unrelated edit must
  // never rewrite EVI's fee, nor trip the echo check against an API
  // that predates the field. A malformed value blocks Save rather than
  // reading as "no fee".
  const storedFeeCents = typeof charger.connection_fee_cents === 'number'
    ? charger.connection_fee_cents
    : null;
  const [feeDollarsStr, setFeeDollarsStr] = useState(
    connectionFeeCentsToDollars(storedFeeCents),
  );
  const feeInput = parseConnectionFeeDollars(feeDollarsStr);
  const feeChanged = feeInput.ok && feeInput.cents !== storedFeeCents;

  // Currently-selected agent row for the Connect-readiness hint.
  // null while loading or before selection.
  const selectedAgent = agentId && agents
    ? agents.find((a) => a.id === agentId) ?? null
    : null;

  const addConnector = () => {
    const nextId = (connectors.reduce((max, c) => Math.max(max, c.connector_id), 0) + 1) || 1;
    setConnectors((cs) => [
      ...cs,
      { connector_id: nextId, type: 'J1772', max_power_kw: 22, status: 'Available', status_updated_at: new Date().toISOString() },
    ]);
  };
  const removeConnector = (idx: number) =>
    setConnectors((cs) => cs.filter((_, i) => i !== idx));
  const updateConnector = (idx: number, patch: Partial<Connector>) =>
    setConnectors((cs) => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  // Markup percent string → bps for the wire. Empty / NaN → null.
  // Caps at 50000 bps (500%) at the form layer so the user gets
  // immediate feedback; server schema enforces the same cap.
  const markupBpsForSave = (() => {
    const trimmed = markupPctStr.trim();
    if (!trimmed) return null;
    const pct = Number(trimmed);
    if (!Number.isFinite(pct) || pct < 0) return null;
    return Math.min(50_000, Math.round(pct * 100));
  })();

  // Home charger detection — the server guard's exact discriminator:
  // Location.type === 'residential', nothing else. created_via is NOT
  // a valid signal here — resident-managed chargers also exist at
  // multifamily buildings, where the markup IS the legitimate pricing
  // model. Only a location confirmed residential hides the control;
  // when the charger's location isn't in the loaded list (cap 100),
  // the input renders and the server guard still rejects a changed
  // value at a home, loudly.
  const isHomeCharger =
    locations.find((l) => l._id === charger.location_id)?.type === 'residential';
  // Each step still missing before a listed charger shows on the map and
  // other drivers can start it (Warren's home charger, 22 Oct 2026).
  const listingBlockers = mapListingBlockers({
    listed: publicNetwork, chargerActive: isActive, openToGuests: isOpenToGuests,
    guestPriceSet: markupPctStr.trim() !== '',
    location: locations.find((l) => l._id === locationId) ?? null,
    hiddenUntilContactSince: charger.map_hidden_until_contact_after ?? null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSuperAdmin && !feeInput.ok) return;
    // omitUnreadEmpty: the pass list and the agent fields go back only if
    // the charger read carried them or they now hold a value. A read the
    // Api stripped for a Site (the key left out) seeds [] / '' here, and
    // sending that would clear the real list. The Edit toggle is already
    // hidden for a client-detected Site; this covers the Site the client
    // cannot detect (see isSiteCallerUser's gap). Staff reads carry the
    // keys, so their payload is unchanged.
    void onSave(omitUnreadEmpty(charger, {
      vendor: vendor.trim(),
      model: model.trim(),
      ocpp_version: ocppVersion.trim(),
      location_id: locationId,
      is_active: isActive,
      connectors,
      is_open_to_guests: isOpenToGuests,
      // Omitted entirely for a home charger — the PUT only $sets keys
      // present in the body, so omission is a no-touch. Sending null
      // here would silently wipe a value; a stray one on a home is
      // cleared deliberately (scripts/clear-guest-markup.ts), not as
      // a side effect of an unrelated admin edit.
      ...(isHomeCharger ? {} : { guest_markup_bps: markupBpsForSave }),
      public_network: publicNetwork,
      // The pass list is not sent: the Private access section owns it
      // (an absent key is a no-touch on the PUT). Time windows round-trip.
      guest_time_windows: guestTimeWindows,
      // Network Origin (Phase 7b). Always send — server PUT strips
      // for non-super_admin. agentId='' → null on the wire (the
      // form-layer convention for "no agent assigned"). When agent
      // cleared, commission also clears to null automatically via
      // commissionBpsForSave's `if (!agentId) return null` guard.
      agent_id: agentId || null,
      agent_commission_bps: commissionBpsForSave,
      // Connection fee: super_admin only and only when changed (see
      // feeChanged). The server strips it for every other caller too.
      ...(isSuperAdmin && feeInput.ok && feeChanged
        ? { connection_fee_cents: feeInput.cents }
        : {}),
    }, ['agent_id', 'agent_commission_bps']));
  };

  return (
    <form className={styles.editCard} onSubmit={handleSubmit}>
      <div className={styles.editTitleRow}>
        <p className={styles.editTitle}>Edit charger</p>
        <span className={styles.editIdentity}>{charger.identity}</span>
      </div>
      {error && <div className={formStyles.formError}>{error}</div>}

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Vendor</label>
          <input className={formStyles.input} value={vendor} onChange={(e) => setVendor(e.target.value)} required />
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Model</label>
          <input className={formStyles.input} value={model} onChange={(e) => setModel(e.target.value)} required />
        </div>
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={formStyles.label}>OCPP version</label>
          <select className={formStyles.input} value={ocppVersion} onChange={(e) => setOcppVersion(e.target.value)}>
            <option value="1.6">OCPP 1.6</option>
            <option value="2.0.1">OCPP 2.0.1</option>
          </select>
        </div>
        <div className={formStyles.field}>
          <label className={`${formStyles.label} ${formStyles.required}`}>Location</label>
          <select className={formStyles.input} value={locationId} onChange={(e) => setLocationId(e.target.value)} required>
            {locations.length === 0 && <option value={charger.location_id}>{charger.location_name}</option>}
            {locations.map((l) => (
              <option key={l._id} value={l._id}>{l.name} — {l.address.city}, {l.address.state}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={formStyles.row}>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Tariff</label>
          <p className={formStyles.hint}>Set in the Pricing section of this page: the location default or a tariff of this charger&apos;s own.</p>
        </div>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Active</label>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            label={isActive ? 'Charger is live and accepting sessions' : 'Disabled: hidden from drivers'}
          />
        </div>
      </div>

      <div className={styles.connectorsEdit}>
        <div className={styles.connectorsEditHeader}>
          <span className={formStyles.label}>Connectors</span>
          <button type="button" className={styles.addConnectorBtn} onClick={addConnector}>
            <Plus size={12} /> Add connector
          </button>
        </div>
        {connectors.length === 0 && (
          <p className={styles.emptyConnectors}>No connectors yet — add at least one.</p>
        )}
        {connectors.map((c, idx) => (
          <div key={idx} className={styles.connectorEditRow}>
            <input
              className={formStyles.input}
              style={{ width: 72 }}
              type="number"
              min={1}
              value={c.connector_id}
              onChange={(e) => updateConnector(idx, { connector_id: Number(e.target.value) })}
              aria-label="Connector ID"
            />
            <select
              className={formStyles.input}
              value={c.type}
              onChange={(e) => updateConnector(idx, { type: e.target.value })}
              aria-label="Connector type"
            >
              <option value="J1772">J1772</option>
              <option value="NACS">NACS</option>
              <option value="CCS1">CCS1</option>
            </select>
            <input
              className={formStyles.input}
              style={{ width: 100 }}
              type="number"
              min={1}
              step="any"
              value={c.max_power_kw}
              onChange={(e) => updateConnector(idx, { max_power_kw: Number(e.target.value) })}
              aria-label="Max kW"
            />
            <span className={styles.kwSuffix}>kW</span>
            <input
              className={formStyles.input}
              style={{ width: 100 }}
              type="number"
              min={0}
              step={1}
              value={c.max_current_a ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') { updateConnector(idx, { max_current_a: null }); return; }
                const n = Number(v);
                if (!Number.isFinite(n)) return;
                updateConnector(idx, { max_current_a: Math.max(0, Math.floor(n)) });
              }}
              aria-label="Max current (A)"
              placeholder="—"
            />
            <span className={styles.kwSuffix}>A</span>
            <button
              type="button"
              className={styles.removeConnectorBtn}
              onClick={() => removeConnector(idx)}
              disabled={connectors.length <= 1}
              title="Remove"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--color-tx-m)' }}>
          Max current (A) is the per-connector ceiling the load-management loop respects. Leave blank for no ceiling.
        </p>
      </div>

      {/* ── Guest access (commit 9 of third-party-access ticket) ──
          Three toggles + markup. Pass list (commit 10) and time
          windows (commit 11) ship in follow-up commits; placeholder
          summaries below indicate where they'll land. */}
      <div className={styles.guestSection}>
        <p className={formStyles.label} style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
          Guest access
        </p>
        <p className={styles.guestSectionHint}>
          {isHomeCharger
            ? 'Outside drivers can charge here when guest access is on. They pay the owner’s guest rate, which the owner sets in their app — there is no markup at a home charger.'
            : 'Outside drivers can charge here when guest access is on. They’ll pay your tenant rate plus the guest pricing markup; revenue lands in your operator account just like a member session.'}
        </p>

        <Switch
          checked={isOpenToGuests}
          onChange={(e) => setIsOpenToGuests(e.target.checked)}
          label={<>
            <strong>Open to guests</strong>
            <span className={styles.toggleHint}>
              {' '}Allow drivers from other tenants to charge here
            </span>
          </>}
        />

        {isHomeCharger ? (
          <p className={styles.fieldHint} style={{ marginTop: 10 }}>
            Home charger — guest pricing is the owner&rsquo;s guest rate,
            set by the owner in their app. The multifamily markup does not
            apply here.
          </p>
        ) : (
          <div className={formStyles.field} style={{ marginTop: 10 }}>
            <label className={formStyles.label}>
              Guest pricing markup (%)
              {!isOpenToGuests && (
                <span className={styles.toggleHint}> — set "Open to guests" first</span>
              )}
            </label>
            <input
              className={formStyles.input}
              type="number"
              min={0}
              max={500}
              step={0.1}
              placeholder="e.g. 20"
              value={markupPctStr}
              onChange={(e) => setMarkupPctStr(e.target.value)}
              disabled={!isOpenToGuests}
            />
            <p className={styles.fieldHint}>
              0–500%. Multiplies the per-kWh rate for guest sessions only;
              tenant members aren&rsquo;t affected. Idle fees and
              EVI&rsquo;s connection fee don&rsquo;t receive markup.
            </p>
          </div>
        )}

        <Switch
          checked={publicNetwork}
          onChange={(e) => setPublicNetwork(e.target.checked)}
          label={<>
            <strong>Public network listing</strong>
            <span className={styles.toggleHint}>
              {' '}Listed publicly by default. Switch off to hide this
              charger from the EVI driver map. It stays reachable via
              direct link or QR either way.
            </span>
          </>}
        />
        {listingBlockers.length > 0 && (
          <div className={styles.fieldHint} role="status" style={{ marginTop: 6 }}>
            <strong>Not on the driver map yet:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {listingBlockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </div>
        )}

        {/* The pass list moved to the Private access section below the
            form (2026-10-23): it now also decides who may start a private
            charger, so it has one editor, with its own audited write and a
            stale-editor check. This form no longer sends it. */}
        <div className={styles.guestSubcard}>
          <p className={formStyles.label} style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            Guest pass list
          </p>
          <p className={styles.fieldHint}>
            Edited in the Private access section on this page. Drivers on the list can charge regardless of the
            master switch or time windows, and at a private charger they are the drivers who may start it.
          </p>
        </div>

        {/* Time windows (commit 11). Inline table mirroring the
            TariffForm TOU pattern, minus the energy_rate field.
            Empty array = no restriction (default permissive when
            "Open to guests" is on). canGuestUseCharger short-
            circuits on first match so overlapping windows are a
            no-op; we don't validate strict-disjoint client-side.
            Field naming: `days_of_week` (plural) per the foundation
            schema. The pre-existing Tariff TOU schedule uses
            `day_of_week` (singular but the value is also an array);
            future cleanup unifies both into a shared timeWindow
            sub-schema (noted in commit 1). */}
        <div className={styles.guestSubcard}>
          <p className={formStyles.label} style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>
            Guest time windows
          </p>
          <p className={styles.fieldHint} style={{ marginBottom: 8 }}>
            Restrict guest charging to specific hours. Leave empty for
            no restriction (always available when guest access is on).
            Pass-list drivers ignore these windows.
          </p>

          <TimeWindowRows<GuestTimeWindowInput>
            windows={guestTimeWindows}
            onChange={setGuestTimeWindows}
            getDays={(w) => w.days_of_week}
            setDays={(w, days) => ({ ...w, days_of_week: days })}
            getStartHour={(w) => w.start_hour}
            setStartHour={(w, h) => ({ ...w, start_hour: h })}
            getEndHour={(w) => w.end_hour}
            setEndHour={(w, h) => ({ ...w, end_hour: h })}
            // 9-17 default mirrors the inline pattern this replaces —
            // standard business hours, empty days so operator picks
            // at least one to make the row meaningful.
            createEmpty={() => ({ days_of_week: [], start_hour: 9, end_hour: 17 })}
            emptyStateText="No restriction — guests can charge any time the master switch is on."
          />
        </div>
      </div>

      {/* ── Network Origin (Phase 7b of agent-commission ticket) ──
          super_admin-only. Renders nothing for tenant_admin so
          EVI's internal commercial relationships stay invisible to
          operators. Reuses the .guestSection / .guestSubcard
          styles for visual parity with the operator-policy block
          above. Tier 2 adds EVI's per-charger connection fee here:
          set by EVI, kept by EVI, so it sits with EVI's other
          commercial controls rather than the operator's pricing. */}
      {isSuperAdmin && (
        <div className={styles.guestSection}>
          <p
            className={`${formStyles.label} ${styles.networkOriginHeader}`}
            style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}
          >
            Network Origin
          </p>
          <p className={styles.guestSectionHint}>
            EVI-internal commercial attribution. Assign a Sales Partner
            to credit them a commission on every session at this
            charger. Commission is computed at session-stop time and
            paid out via Stripe Connect (subject to the Sales Partner
            completing onboarding). Hidden from tenant operators.
          </p>

          {/* Agent picker — simple select; agents list is small.
              Upgrade to UserTypeahead later if the rolodex grows
              past ~30 entries. */}
          <div className={formStyles.field} style={{ marginTop: 8 }}>
            <label className={formStyles.label}>Assigned Sales Partner</label>
            {agentsLoadErr ? (
              <div className={formStyles.formError}>{agentsLoadErr}</div>
            ) : (
              <select
                className={formStyles.input}
                value={agentId}
                onChange={(e) => {
                  const next = e.target.value;
                  setAgentId(next);
                  // Clear commission when agent cleared — the field is
                  // meaningless without an agent and the wire payload
                  // drops it to null anyway via commissionBpsForSave.
                  if (!next) setCommissionPctStr('');
                }}
                disabled={agentsLoading || agents === null || charger.agent_commission_bps != null}
              >
                <option value="">— Direct sale (no Sales Partner) —</option>
                {agents
                  ?.filter((a) => a.active)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.email})
                    </option>
                  ))}
                {agents && agents.some((a) => !a.active) && (
                  <optgroup label="Inactive">
                    {agents
                      .filter((a) => !a.active)
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.email}) (inactive)
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            )}
            {/* Connect-readiness hint on the selected agent. Only
                renders when an agent is picked AND we've hydrated
                the agent row from the list response. */}
            {selectedAgent && (
              <p className={styles.fieldHint} style={{ marginTop: 6 }}>
                {selectedAgent.stripe_connect_account_id
                  ? '✓ Stripe Connect ready — commissions transfer immediately'
                  : '⚠ Stripe Connect onboarding incomplete — commissions queue as pending_stripe_setup until the Sales Partner finishes setup'}
              </p>
            )}
          </div>

          {/* Commission percent input — parallel to guest_markup_bps.
              Disabled when no agent selected. */}
          <div className={formStyles.field} style={{ marginTop: 10 }}>
            <label className={formStyles.label}>
              Sales Partner commission (%)
              {!agentId && (
                <span className={styles.toggleHint}>
                  {' '}— assign a Sales Partner first
                </span>
              )}
            </label>
            <input
              className={formStyles.input}
              type="number"
              min={0}
              max={3}
              step={0.1}
              placeholder="e.g. 3"
              value={commissionPctStr}
              onChange={(e) => setCommissionPctStr(e.target.value)}
              disabled={!agentId || charger.agent_commission_bps != null}
            />
            <p className={styles.fieldHint}>
              Default maximum 3%. Golden rates up to 5% are assigned at launch. Existing launch rates stay fixed.
            </p>
          </div>

          {/* EVI's connection fee (Tier 2). Dollars in the field, cents
              on the wire; blank = no fee. Text input, not type=number:
              a browser reports malformed number input as '', which
              would read as "no fee" (see lib/connectionFeeInput.ts). */}
          <div className={formStyles.field} style={{ marginTop: 10 }}>
            <label className={formStyles.label} htmlFor="charger-connection-fee">
              Connection fee ($)
            </label>
            <input
              id="charger-connection-fee"
              className={formStyles.input}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="blank = no fee"
              value={feeDollarsStr}
              onChange={(e) => setFeeDollarsStr(e.target.value)}
              aria-invalid={!feeInput.ok}
              style={{
                fontVariantNumeric: 'tabular-nums',
                ...(feeInput.ok ? {} : { borderColor: 'var(--color-error)' }),
              }}
            />
            <p
              className={styles.fieldHint}
              style={feeInput.ok ? undefined : { color: 'var(--color-error)' }}
            >
              {feeInput.ok
                ? <>
                    Paid sessions only · kept by EVI · pinned at session start.
                    $0.00–{formatConnectionFee(CONNECTION_FEE_MAX_CENTS)}; blank = no
                    fee. New chargers start
                    at {formatConnectionFee(DEFAULT_CONNECTION_FEE_CENTS)}.
                    {feeChanged && <> Currently {formatConnectionFee(storedFeeCents)}.</>}
                  </>
                : feeInput.message}
            </p>
          </div>
        </div>
      )}

      <div className={styles.editActions}>
        <button type="button" className={formStyles.btnSecondary} onClick={onCancel} disabled={saving}>
          <X size={13} /> Cancel
        </button>
        <button
          type="submit"
          className={formStyles.btnPrimary}
          disabled={saving || !vendor || !model || connectors.length === 0 || (isSuperAdmin && !feeInput.ok)}
        >
          {saving ? 'Saving…' : <><Check size={13} /> Save changes</>}
        </button>
      </div>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ActiveSessionPill — live "charging now" indicator on connector cards
// ══════════════════════════════════════════════════════════════════════════

/**
 * Renders a pulsing "Charging now" pill + "Open session" link whenever
 * there's an active session on this connector. Renders nothing otherwise.
 *
 * Reads `/chargers/:id/active-session`, refetched by live session
 * events (LiveUpdatesProvider). The query key is scoped by charger
 * (the endpoint returns the charger's one active session — we filter
 * by connector client-side so multiple connector cards share a single
 * cached response instead of firing N parallel requests).
 *
 * Per Track B design: spartan. No inline stats. All telemetry lives
 * on SessionDetailPage.
 */
function ActiveSessionPill({
  chargerId,
  connectorId,
  onNavigate,
}: {
  chargerId: string;
  connectorId: number;
  onNavigate: (path: string) => void;
}) {
  // Live: session events refetch it (LIVE_QUERY_KEYS); no poll.
  const { data } = useQuery({
    queryKey: ['charger', chargerId, 'active-session'],
    queryFn: () => fetchActiveSessionForCharger(chargerId),
    staleTime: 5_000,
  });

  if (!data || data.connector_id !== connectorId) return null;

  return (
    <div className={styles.activeSessionPill}>
      <span className={styles.activeSessionBadge}>
        <span className={styles.activeSessionDot} />
        Charging now
      </span>
      <button
        type="button"
        className={styles.activeSessionLink}
        onClick={() => onNavigate(`/sessions/${data._id}`)}
      >
        Open session <ArrowRight size={11} />
      </button>
    </div>
  );
}

// ── DLM cap ───────────────────────────────────────────────────────

/**
 * The amperage cap this platform has installed on the charger, and whether
 * anything still owns it.
 *
 * WHY THIS IS ON THE CHARGER PAGE. The cap used to render only inside
 * BuildingCircuitsSection and FleetDetailPage — both of which list circuit
 * MEMBERS. A charger whose circuit was deleted has no membership, so it
 * appeared in neither, and the one fact that explained its behaviour was
 * visible nowhere in the product. CU89TVWKPVJ8 sat at 6 A against a 32 A
 * connector from 2026-06-03 to 2026-08-01 for exactly that reason: every
 * surface that could have shown the cap was keyed on the relationship whose
 * disappearance caused the bug.
 *
 * So it lives here, on the charger, keyed on the charger. Renders nothing at
 * all when there is no cap — an uncapped charger should not carry a row of
 * empty DLM fields.
 */
function DlmCapCard({ charger }: { charger: ChargerDetail }) {
  const cap = charger.dlm_last_cap_a;
  if (typeof cap !== 'number') return null;

  const circuit = charger.dlm_circuit ?? null;
  const releaseOwed = charger.dlm_cap_intent === 'release';
  // null/absent source reads as 'dlm' — the loop was the only writer before
  // dlm_cap_source existed, so legacy rows are genuinely ours.
  const source = charger.dlm_cap_source ?? 'dlm';
  const isDlmCap = source === 'dlm';
  // ORPHAN: a cap THIS LOOP installed, that nothing owns. A deactivated
  // circuit counts — dlmRecompute only loads active circuits, so its members
  // are just as unreachable as a deleted circuit's.
  //
  // The source test is not cosmetic: a residential charger legitimately has no
  // circuit, so without it every My Home amperage cap would render as a
  // stranded cap needing attention. An operator's or a resident's cap is
  // OWNED — by them — and is not this system's unfinished work.
  const orphaned = isDlmCap && (!circuit || !circuit.is_active);
  const SOURCE_LABEL: Record<string, string> = {
    admin: 'set by an operator from the Commands tab',
    owner: 'set by the resident from My Home',
  };

  const pushedAt = charger.dlm_last_pushed_at;
  const pushedAgo = pushedAt ? timeAgo(pushedAt) : null;
  const pushedAbs = pushedAt ? new Date(pushedAt).toLocaleString() : null;

  return (
    <div className={`${styles.dlmCap} ${orphaned ? styles.dlmCapOrphan : ''}`}>
      <div className={styles.dlmCapHead}>
        {orphaned ? <AlertTriangle size={16} /> : <Gauge size={16} />}
        <span className={styles.dlmCapTitle}>
          {orphaned ? 'Amperage cap with no owning circuit' : 'Amperage cap'}
        </span>
        <span className={styles.dlmCapValue}>{cap} A</span>
      </div>

      <p className={styles.dlmCapLine}>
        This charger is limited to <strong>{cap} A</strong> by a charging
        profile this platform installed
        {pushedAgo ? <> {pushedAgo}</> : null}
        {pushedAbs ? <span className={styles.dlmCapWhen}> ({pushedAbs})</span> : null}.
      </p>

      {!isDlmCap && (
        <p className={styles.dlmCapLine}>
          This cap was <strong>{SOURCE_LABEL[source] ?? `set outside the control loop (${source})`}</strong>,
          not by a circuit budget. The DLM loop leaves it alone and will not
          recompute it.
        </p>
      )}

      {isDlmCap && circuit && circuit.is_active && (
        <p className={styles.dlmCapLine}>
          Owned by circuit <strong>{circuit.name}</strong> ({circuit.limit_a} A budget).
          The control loop recomputes it automatically.
        </p>
      )}

      {isDlmCap && circuit && !circuit.is_active && (
        <p className={styles.dlmCapLine}>
          Circuit <strong>{circuit.name}</strong> is <strong>deactivated</strong>, so the
          control loop no longer manages this charger — but the cap is still
          installed on the hardware.
        </p>
      )}

      {isDlmCap && !circuit && (
        <p className={styles.dlmCapLine}>
          <strong>No circuit owns this cap.</strong> The circuit it came from was
          deleted or the charger was unassigned, so nothing will recompute or
          remove it on its own. The charger will keep delivering {cap} A until
          it is released.
        </p>
      )}

      {orphaned && (
        <p className={styles.dlmCapLine}>
          {releaseOwed
            ? 'A release is queued. The platform retries every 5 minutes and will '
              + 'clear it the next time this charger is connected.'
            : 'No release is queued yet. Run the orphaned-cap migration to release it, '
              + 'or clear it by hand from the Commands tab.'}
        </p>
      )}
    </div>
  );
}
